import React from "react";
import {
  Grid,
  Typography,
  Divider,
  FormControl,
  InputAdornment,
  Box,
} from "@mui/material";
import { Controller, UseFormReturn } from "react-hook-form";
import SkeletonWrapper from "../Skeleton/SkeletonWrapper";
import { CustomSelect } from "../CustomFields/CustomSelect";
import { CustomTextField } from "../CustomFields/TextField";
import { createInputPropsForNumberField } from "../../shared/data";

// ---------- Type Definitions ----------

// Option type used in your CustomSelects
interface OptionType {
  value: string;
  label: string;
}

// Form data structure
interface AvailabilityData {
  durationValue?: OptionType | null;
  customValue?: string | number;
  customType?: OptionType | null;
}

// React Hook Form methods
interface AppointmentDurationSectionProps {
  methods: UseFormReturn<{
    availabilityData: AvailabilityData;
  }>;
  showSkeleton: boolean;
  durationOptions: OptionType[];
  durationTypeOptions: OptionType[];
  customDuration: boolean;
  setCustomDuration: React.Dispatch<React.SetStateAction<boolean>>;
}
const prProps = createInputPropsForNumberField(3, 0, false);
// ---------- Component ----------
const AppointmentDurationSection: React.FC<AppointmentDurationSectionProps> = ({
  methods,
  showSkeleton,
  durationOptions,
  durationTypeOptions,
  customDuration,
  setCustomDuration,
}) => {
  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h6">Appointment Duration</Typography>
        <Typography className="font-size-12">
          How long should each appointment last?
        </Typography>
      </Grid>

      {/* Duration dropdown */}
      <Grid item xs={12} sm={6} md={3}>
        <FormControl fullWidth>
          <SkeletonWrapper isLoading={showSkeleton} className="skeleton-input">
            <Controller
              control={methods.control}
              name={`availabilityData.durationValue`}
              render={({ field }) => (
                <CustomSelect
                  {...field}
                  isNotCreatable
                  id="durationValue"
                  FieldName="durationValue"
                  options={durationOptions}
                  handleChange={(e: OptionType | null) => {
                    if (e?.value === "CUSTOM") setCustomDuration(true);
                    else setCustomDuration(false);

                    const currentVal = methods
                      .getValues()
                      ?.availabilityData?.durationValue?.value?.toString();

                    if (currentVal !== e?.value?.toString()) {
                      methods.setValue(
                        "availabilityData.durationValue",
                        e?.value ? e : null,
                        { shouldValidate: true }
                      );

                      const myArray = e?.value?.split("_");
                      if (myArray && myArray[0]) {
                        methods.setValue(
                          "availabilityData.customValue",
                          myArray[0] ?? 10
                        );
                      }
                      if (myArray && myArray[1]) {
                        methods.setValue("availabilityData.customType", {
                          value: myArray[1],
                          label: myArray[1] === "HOURS" ? "Hours" : "Minutes",
                        });
                      }
                    }
                  }}
                />
              )}
            />
          </SkeletonWrapper>
        </FormControl>
      </Grid>

      {/* Custom duration fields */}
      {customDuration && (
        <>
          <Grid item xs={12} sm={6} md={3.5} lg={3.5} xl={2.5}>
            <FormControl fullWidth>
              <SkeletonWrapper
                isLoading={showSkeleton}
                className="skeleton-input"
              >
                <CustomTextField
                  placeholder="Enter value"
                  fullWidth
                  id="customValue"
                  size="small"
                  type="number"
                  inputProps={prProps}
                  {...methods.register("availabilityData.customValue", {
                    onChange(event) {
                      methods.setValue(
                        "availabilityData.customValue",
                        event.target.value,
                        { shouldValidate: true }
                      );
                    },
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Box
                          sx={{
                            display: "flex",
                            minWidth: "170px",
                          }}
                        >
                          <Box
                            className="input-end-andorment-border"
                            sx={{
                              width: "100%",
                            }}
                          >
                            <Controller
                              control={methods.control}
                              name="availabilityData.customType"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isBorderNone
                                  isNotCreatable
                                  isSearchable={false}
                                  isIndicatorSeparator={false}
                                  id="customType"
                                  FieldName="customType"
                                  options={durationTypeOptions}
                                  handleChange={(e: OptionType | null) => {
                                    const curr =
                                      methods.getValues()?.availabilityData
                                        ?.customType?.value;
                                    if (curr !== e?.value) {
                                      methods.setValue(
                                        "availabilityData.customType",
                                        e?.value ? e : null,
                                        { shouldValidate: true }
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </Box>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: "150px",
                    ".MuiOutlinedInput-root": {
                      paddingRight: 0,
                      paddingLeft: 0,
                    },
                    "& input": {
                      textAlign: "end",
                    },
                  }}
                  error={
                    !!methods.formState.errors?.availabilityData?.customValue
                  }
                />
              </SkeletonWrapper>
            </FormControl>
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <Divider />
      </Grid>
    </>
  );
};

export default AppointmentDurationSection;
